import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslator } from '@jutro/locale';
import { useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import _ from 'lodash';
import cx from 'classnames';
import messages from '../../MLSummary.messages'
import { MLProductCodeUtil } from 'ml-portals-util-js';
import styles from '../../MLSummary.module.scss'
import { Button } from '@jutro/legacy/components';

function MLVehicleCoverages({ period, show, getFormattedCurrency, onRight, onLeft }) {
    const translator = useTranslator();
    const { refreshData } = useDataRefresh();
    const isModernProductAuto = period.isModernProduct_Ext && period.policyProductCode_ML==="PersonalAuto"

    const displayedLineCoverages = new Map();
    const termTypeLabels = {
        Annual: translator(messages.YearTerm),
        HalfYear: translator(messages.SixMonthTerm)
    };

    if (_.isEmpty(period)) {
        return null;
    }

    const covAmountIsZero = (cov) => {
        let amt = 0;
        if (cov.lineCovCost_Ext && cov.lineCovCost_Ext.amount) {
            amt = cov.lineCovCost_Ext.amount;
        }
        return amt === 0;
    };
    const isCamperTrailer = (veh) => {
        return veh?.vehicleCategoryCode_Ext === 'camper_np' || veh?.vehicleCategoryCode_Ext === 'trailer_np' || veh?.vehicleCategoryCode_Ext === 'trailer';
    };

    const isStorageVehicle = (veh) => {
        const isStorage = veh?.vehiclePrimaryUse_Ext === 'storage_np';
        return !!isStorage || !!isCamperTrailer(veh);
    };

    const isStorageVehicleOnly = (veh) => {
        const isStorage = veh?.vehiclePrimaryUse_Ext === 'storage_np';
        return !!isStorage;
    };
    
    const isRucoo = (coverage) => {
        return !!(coverage?.code_ML === "PARegUseCarsOwnedByOthers_Ext")
    };

    const shouldHideForStorageCamperTrailer = (coverage, vehCov) => {
        return ((coverage?.code_ML === 'PARegUseCarsOwnedByOthers_Ext' || coverage?.code_ML === 'PATravelCov_ML') && (isStorageVehicle(vehCov) || isCamperTrailer(vehCov))
            || (coverage?.code_ML === 'PADecliningDeductibleCov_Ext' && isCamperTrailer(vehCov)));
    }

    const hasSuspendedLiability = (veh) => {
        // look at selected attribute for Suspended Liability coverage using vehicle public id to look up
        const suspendLiabCov = veh.coverages.find((cov) => cov.code_ML === 'PASuspendLiabilityCov_ML');
        return !!suspendLiabCov && suspendLiabCov.selected;
    };

    const covAmountDisplayed = (coverage, vehicle) => {
        if (displayedLineCoverages.has(coverage.code_ML)) {
            const vehId = displayedLineCoverages.get(coverage.code_ML);
            return vehId !== vehicle.publicID;
        }
        return false;
    };

    const getPolicyLevelCostForCoverage = (coverage, vehicle) => {
        if (isCamperTrailer(vehicle) || hasSuspendedLiability(vehicle) || covAmountIsZero(coverage)) {
            return;
        }
        if (!covAmountDisplayed(coverage, vehicle)) {
            displayedLineCoverages.set(coverage.code_ML, vehicle.publicID);
            return getFormattedCurrency(coverage.lineCovCost_Ext);
        }
        return 'Included';
    };

    const getVehicleMonthlyPremium = (coverage, vehicle) => {
        let totalLineCovPremium;
        let tempValue;
        let returnObj = {};
        let totalVehMonthlyPremium;
        coverage.forEach((cov) => {
            if (cov.isLineCovCost_Ext === true) {
                if (covAmountDisplayed(cov, vehicle)) {
                    tempValue = cov?.lineLevelMonthlyCost_Ext?.amount ?? 0;
                    displayedLineCoverages.set(cov.code_ML, vehicle.publicID);
                    totalLineCovPremium = totalLineCovPremium === undefined ? 0 : totalLineCovPremium;
                    totalLineCovPremium += tempValue;
                    totalVehMonthlyPremium = totalLineCovPremium + vehicle.monthlyCost_ML.amount;
                    returnObj = {
                        amount: totalVehMonthlyPremium,
                        currency: 'usd'
                    };
                } else {
                    returnObj = vehicle.monthlyCost_ML;
                }
            } else {
                returnObj = vehicle.monthlyCost_ML;
            }
        });
        return returnObj;
    };

    const getVehicleYearlyPremium = (coverage, vehicle) => {
        let totalLineCovPremium;
        let tempValue;
        let returnObj = {};
        let totalVehYearlyPremium;
        coverage.forEach((cov) => {
            if (cov.isLineCovCost_Ext === true) {
                tempValue = cov.lineCovCost_Ext.amount;
                if (!covAmountDisplayed(cov, vehicle)) {
                    displayedLineCoverages.set(cov.code_ML, vehicle.publicID);
                    totalLineCovPremium = totalLineCovPremium === undefined ? 0 : totalLineCovPremium;
                    totalLineCovPremium += tempValue;
                    totalVehYearlyPremium = totalLineCovPremium + vehicle.vehicleCost_ML.amount;
                    returnObj = {
                        amount: totalVehYearlyPremium,
                        currency: 'usd'
                    };
                } else {
                    returnObj = vehicle.vehicleCost_ML;
                }
            } else {
                returnObj = vehicle.vehicleCost_ML;
            }
        });
        return returnObj;
    };

    const getLableName = (nav) => {
        if (period.numberOfVehicles_ML > 0) {
            return nav.vehicleName
        }
        else {
            return period.isModernProduct_Ext ? translator(messages.namedNonOwnerNP) : translator(messages.namedNonOwner)
        }
    }

    const generateHeader = () => {
        return (
            <thead>
                <tr>
                    <td>
                        {(period.numberOfVehicles_ML > 0)?

                            <h2 className="section-header vertical-spacing-20">
                                {translator(messages.VehicleCoverages)}
                            </h2> : 
                            <></>
                        }
                        {(period.numberOfVehicles_ML === 0)?
                            <h2 className="section-header vertical-spacing-20">
                                {translator(messages.Coverages)}
                            </h2> :
                            <></>
                        }
                    </td>
                    <td>
                        <header className="ml-scrolling-table__header">
                            <Button
                                disabled={!period.vehNavigation_ML.hasLeft()}
                                className={cx(
                                    "ml-scrolling-table__header_scroller-control", 
                                    "prev",
                                    styles.prevButton,
                                    { "inactive": !period.vehNavigation_ML.hasLeft() }
                                )}
                                onClick={() => {
                                    period.vehNavigation_ML.hasLeft() && period.vehNavigation_ML.goLeft()
                                    refreshData();
                                }}
                            />
                            <Button
                                disabled={!period.vehNavigation_ML.hasRight()}
                                className={cx(
                                    "ml-scrolling-table__header_scroller-control", 
                                    "next",
                                    styles.nextButton,
                                    { "inactive": !period.vehNavigation_ML.hasRight() }
                                )}
                                onClick={() => {
                                    period.vehNavigation_ML.hasRight() && period.vehNavigation_ML.goRight()
                                    refreshData();
                                }}
                            />
                            <div className={cx("ml-scrolling-table__header_scroller clearfix", styles.displayFlex)}>
                                {period.vehNavigation_ML.current.map((nav) => {
                                    return (
                                        <div className="col-sm-4">
                                            <span className="ml-count-icon large condensed ml-scrolling-table__header_scroller-count">{nav.vehicleNumber_ML}</span>
                                            <span className="ml-scrolling-table__header_scroller-vehicle-name">{getLableName(nav)}</span>
                                            <span className="ml-scrolling-table__header_scroller-vehicle-trim">{nav.vehicleTrim_ML}</span>
                                        </div>
                                    )
                                })}
                            </div>
                        </header>
                    </td>
                </tr>
                <tr>
                    <td>
                        <strong className="ml-scrolling-table__coverage-items-title" ng-if="period.numberOfVehicles_ML === 0">{(period.numberOfVehicles_ML === 0)? translator(messages.CoverageTotal): translator(messages.VehicleTotal)}</strong>
                    </td>
                    <td>
                        <div className="row">
                            {_.filter(period.lobs_ML.personalAuto.vehicleCoverages, period.vehNavigation_ML.filter).map((vehicleCov) => {
                                return (
                                    <div className="col-md-4">
                                        <span className="ml-scrolling-table__subheader-monthly-total">
                                            {getFormattedCurrency(getVehicleMonthlyPremium(period.lobs_ML.personalAuto.lineCoverages, vehicleCov))}{translator(messages.MonthTerm)}
                                        </span>
                                        <span className="ml-scrolling-table__subheader-yearly-total">
                                            {getFormattedCurrency(getVehicleYearlyPremium(period.lobs_ML.personalAuto.lineCoverages, vehicleCov))}{termTypeLabels[period.termType_ML]}
                                        </span>
                                    </div>
                                )
                            })}
                        </div>
                    </td>
                </tr>
            </thead>
        );
    }

    const generateLineCoverageTerm = (term, hideCost = false) => {
        return (
            <div>
                <div>
                 {(term.valueType === 'Other' || term.codeIdentifier_ML.startsWith('PAPIP')) ? <span>{term.name} :</span>: <></>}{term.chosenTermValue}
                </div>
                {!_.isUndefined(term.termCost_Ext) && !hideCost ?
                    <div>
                       ({ getFormattedCurrency(term.termCost_Ext) })
                    </div>
                    : <></>
                }
            </div>
         )
    }

    const generateVehicleNavigationInfo = (coverage) => {
        return period.vehNavigation_ML.current.map((nav) => {
            const lineCoverageArray = period.vehicleCoverages_ML.lineCoveragesByID[coverage.publicID];
            return (
                <div className="col-md-4">
                    {(nav && coverage.isLineCovCost_Ext)?
                        <div>
                            { getPolicyLevelCostForCoverage(coverage, nav) }
                        </div> : 
                        <></>
                    }
                    <div>
                            {coverage.code_ML === 'PAIncomeDisabilityCov_Ext' ? lineCoverageArray.terms[0]?.chosenTermValue: (lineCoverageArray.terms[0].codeIdentifier_ML.startsWith('PAPIP')  ? generateLineCoverageTerm(lineCoverageArray.terms[0])  : lineCoverageArray.terms[0]?.chosenTermValue)}
                    </div>
                    {(lineCoverageArray.vehicleCovCostsByID[nav.publicID] && lineCoverageArray.vehicleCovCostsByID[nav.publicID].amount)?
                        <div>
                            ({getFormattedCurrency(lineCoverageArray.vehicleCovCostsByID[nav.publicID])})
                        </div> :
                        <></>
                    }
                    {(lineCoverageArray.terms.length > 1)?
                        lineCoverageArray.terms.slice(1).map((term) => {
                            if(!(term.name.includes("Undisclosed Driver") || term.codeIdentifier_ML.includes("UndisDrv"))) {
                                return generateLineCoverageTerm(term)
                            }
                        }):
                        <></>
                    }
                </div>

            )
        });
    }

    const generateLineCoverages = () => {
        return period.vehicleCoverages_ML.lineCoverages.map((coverage) => {
            return (
                <tr>
                    <td>
                        <i className="icon-ml-status-check-complete icon-green icon-inline" /> <strong>{coverage.name}</strong>
                    </td>
                    <td>
                        <div className="row">
                            {generateVehicleNavigationInfo(coverage)}
                        </div>
                    </td>
                </tr>
            );
        });
    }

    const hasRUCOOOnAnotherVehicle = (vehicleCoveragesArray, vehicleCov) =>
        Object.entries(vehicleCoveragesArray).some(([vehiclePublicID, coverages]) =>
            vehiclePublicID !== vehicleCov.publicID && Object.values(coverages || {}).some(cov => cov?.code_ML === "PARegUseCarsOwnedByOthers_Ext")
        )
    
    const isAdditionalRUCOOVehicle = (vehicleCoveragesArray, vehicleCov) => {
        let foundCurrentVehicle = false
    
        return Object.entries(vehicleCoveragesArray).some(
            ([vehiclePublicID, coverages]) => {
                if (vehiclePublicID === vehicleCov?.publicID) {
                    foundCurrentVehicle = true
                    return false
                }
    
                if (foundCurrentVehicle) {
                    return false
                }
    
                return Object.values(coverages || {}).some(
                    cov =>
                        cov?.code_ML === 'PARegUseCarsOwnedByOthers_Ext' &&
                        cov?.selected
                )
            }
        )
    }

    const getChosenTermValue = (vehicleCoveragesArray, vehicleCov, coverage) => {
        const chosenTermValue = vehicleCoveragesArray[vehicleCov.publicID][coverage.publicID].terms[0]?.chosenTermValue
        const isRucooCoverage = isRucoo(coverage)
        const coverageCode = coverage?.code_ML
        if(period?.isModernProduct_Ext) {
            const storageCovExcept = ["PAComprehensiveCov", "PACollisionCov", "PANewOriginalPartsCov_Ext", "PATowandRoadServCov_EXT", "PARoadAssistCov_ML", "PAZeroGlassDeductibleCov_Ext", "PADecliningDeductibleCov_Ext"]
            const camperCovExcept = ["PAComprehensiveCov", "PACollisionCov", "PAContentFireCov_Ext", "PATowandRoadServCov_EXT", "PARoadAssistCov_ML"]
            if(isStorageVehicleOnly(vehicleCov)) {
                if(isRucooCoverage) {
                    return hasRUCOOOnAnotherVehicle(vehicleCoveragesArray, vehicleCov) ? translator(messages.NotIncluded) : chosenTermValue
                }
                return !storageCovExcept.includes(coverageCode) ? translator(messages.NotIncluded) : chosenTermValue
            } else if(isCamperTrailer(vehicleCov)) {
                if(isRucooCoverage) {
                    return hasRUCOOOnAnotherVehicle(vehicleCoveragesArray, vehicleCov) ? translator(messages.NotIncluded) : chosenTermValue
                }
                return !camperCovExcept.includes(coverageCode) ? translator(messages.NotIncluded) : chosenTermValue
            } else if(coverageCode === 'PAAgreedValueCov_ML'){
                return translator(messages.Included)
            } else if(isRucooCoverage && isAdditionalRUCOOVehicle(vehicleCoveragesArray, vehicleCov)){
                return translator(messages.Included)
            }
        }
        return chosenTermValue
    }

    const hasCompOrCollisionForVehicle = (vehicleCoveragesArray, vehicleCov) => {
    const vehicleCoverageMap = vehicleCoveragesArray?.[vehicleCov.publicID] || {};
 
    return Object.values(vehicleCoverageMap).some((cov) =>
        (cov?.code_ML === 'PAComprehensiveCov' ||
         cov?.code_ML === 'PACollisionCov') &&
        (cov?.selected === true || _.isUndefined(cov?.selected))
    );
};
    const generateUniqueCoverages = () => {
      const hasCompOrCollisionForVehicle = (
        vehicleCoveragesArray,
        vehicleCov,
      ) => {
        const vehicleCoverageMap =
          vehicleCoveragesArray?.[vehicleCov.publicID] || {};

        return Object.values(vehicleCoverageMap).some((cov) => {
          const isCompOrCollision =
            cov?.code_ML === "PAComprehensiveCov" ||
            cov?.code_ML === "PACollisionCov";

          const isCoverageActive =
            cov?.selected === true || _.isUndefined(cov?.selected);

          return isCompOrCollision && isCoverageActive;
        });
      };

      const isDecliningOrLimitedMexico = (coverage) => {
        return (
          coverage?.code_ML === "PADecliningDeductibleCov_Ext" ||
          coverage?.name === "Declining Deductible" ||
          coverage?.code_ML === "PALimitedMexicoCov_Ext" ||
          coverage?.name === "Limited Mexico Coverage"
        );
      };

      return period.vehicleCoverages_ML.uniqueCoverages.map((coverage) => {
        return (
          <tr key={coverage.publicID || coverage.code_ML}>
            <td>
              <i className="icon-ml-status-check-complete icon-green icon-inline" />
              {coverage.name}
            </td>
            <td>
              <div className="row">
                {_.filter(
                  period.lobs_ML.personalAuto.vehicleCoverages,
                  period.vehNavigation_ML.filter,
                ).map((vehicleCov) => {
                  const vehicleCoveragesArray =
                    period.vehicleCoverages_ML.vehicleCoveragesByID;

                  const currentCoverage =
                    vehicleCoveragesArray?.[vehicleCov.publicID]?.[
                      coverage.publicID
                    ];

                  const hideCoverageValue =
                    isModernProductAuto &&
                    isDecliningOrLimitedMexico(coverage) &&
                    !hasCompOrCollisionForVehicle(
                      vehicleCoveragesArray,
                      vehicleCov,
                    );

                  return (
                    <div
                      key={`${coverage.publicID}-${vehicleCov.publicID}`}
                      className="col-md-4"
                    >
                      {currentCoverage &&
                      !shouldHideForStorageCamperTrailer(
                        currentCoverage,
                        vehicleCov,
                      ) ? (
                        <div>
                          {!hideCoverageValue && (
                            <div>
                              {getChosenTermValue(
                                vehicleCoveragesArray,
                                vehicleCov,
                                coverage,
                              )}
                            </div>
                          )}

                          {!hideCoverageValue &&
                          currentCoverage.amount &&
                          !currentCoverage.isTermLevelCost_Ext &&
                          coverage.coverageCategoryCode !== "PAOptCovGrp_ML" ? (
                            <div>
                              ({getFormattedCurrency(currentCoverage.amount)})
                            </div>
                          ) : null}

                          {!hideCoverageValue &&
                          coverage.coverageCategoryCode === "PAOptCovGrp_ML" &&
                          !coverage.isTermLevelCost_Ext &&
                          currentCoverage?.vehicleCovCostsByID?.[
                            vehicleCov.publicID
                          ]?.amount ? (
                            <div>
                              (
                              {getFormattedCurrency(
                                currentCoverage.vehicleCovCostsByID[
                                  vehicleCov.publicID
                                ],
                              )}
                              )
                            </div>
                          ) : null}

                          {!hideCoverageValue &&
                          currentCoverage.terms?.[0]?.termCost_Ext &&
                          currentCoverage?.vehicleCovCostsByID?.[
                            vehicleCov.publicID
                          ]?.amount ? (
                            <div>
                              (
                              {getFormattedCurrency(
                                currentCoverage.terms[0].termCost_Ext,
                              )}
                              )
                            </div>
                          ) : null}

                          {!hideCoverageValue &&
                          currentCoverage.terms?.length > 1
                            ? currentCoverage.terms.slice(1).map((term) => {
                                if (
                                  !term.name?.includes("Undisclosed Driver")
                                ) {
                                  return (
                                    <React.Fragment
                                      key={term.codeIdentifier_ML || term.name}
                                    >
                                      {generateLineCoverageTerm(
                                        term,
                                        !currentCoverage?.vehicleCovCostsByID?.[
                                          vehicleCov.publicID
                                        ]?.amount,
                                      )}
                                    </React.Fragment>
                                  );
                                }

                                return null;
                              })
                            : null}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </td>
          </tr>
        );
      });
    };
    
    if (show) {
        return (
            <div className="vertical-spacing-40">
                <table className={cx('table', styles.tableDataVertical, styles.tableStripedVertical)}>
                    {generateHeader()}
                    <tbody>
                        {generateLineCoverages()}
                        {generateUniqueCoverages()}
                    </tbody>
                </table>
            </div>
        );
    }
    return ( <></> );
}

MLVehicleCoverages.propTypes = {
    period: PropTypes.shape({}),
    show: PropTypes.bool,
    getFormattedCurrency: PropTypes.func.isRequired
};
MLVehicleCoverages.defaultProps = {
    period: {},
    show: false
};
export default MLVehicleCoverages;
